/**
 * Responsibility: describe the city biome — its ground, its colours, and where its towers
 * stand.
 *
 * The first biome whose character comes from structures rather than from elevation. Contains
 * no Three.js and no DOM access: it emits plain building descriptions, and the render layer
 * never learns that a city is different from a mountain.
 *
 * ## The grid
 *
 * Blocks are laid out on multiples of `CITY.BLOCK_SIZE` in absolute world space. Not per tile
 * — a grid that restarted at each tile edge would put a visible break in the street pattern
 * every 1600 units.
 *
 * ## One tile owns each building
 *
 * A block belongs to whichever tile contains its *centre*. Tile size is not a multiple of
 * block size, so blocks straddle tile edges constantly; without a single owner every straddling
 * building would either be emitted twice by two tiles or clipped in half by both. Buildings
 * near an edge simply extend past it, which is harmless because tile bounding spheres are
 * computed from actual vertex positions.
 *
 * ## Thinning out rather than fading
 *
 * Both whether a block is built on and how tall its tower is scale with the city's biome
 * weight. Approaching a city therefore means scattered low outbuildings that grow denser and
 * taller, which is the steering file's requirement — density falls off, opacity never does.
 */

import { createFbm } from '../noise.js';
import { createLocalRng, deriveSeed } from '../rng.js';
import { CITY, CITY_SHAPES, PALETTE } from '../../config.js';
import { getBuildingShapeOrDefault, baseFootprintPoints } from '../buildingShapes.js';

/** Corners of a building's footprint, in the winding order `emitBox` expects. */
const CORNER_OFFSETS = [
  [-1, -1],
  [1, -1],
  [1, 1],
  [-1, 1],
];

/** Linear interpolation, for turning a 0..1 roll into a range. */
function lerp(from, to, t) {
  return from + (to - from) * t;
}

/** Total weight of a pool, for a weighted pick. */
function poolWeight(pool) {
  return pool.reduce((sum, entry) => sum + entry.weight, 0);
}

/*
  Pool totals are fixed by config, so they are computed once. Doing it per block would be a needless
  reduce inside the hottest loop in city generation.
*/
const COMMON_WEIGHT = poolWeight(CITY_SHAPES.COMMON);
const LANDMARK_WEIGHT = poolWeight(CITY_SHAPES.LANDMARKS);
const LOW_RISE = new Set(CITY_SHAPES.LOW_RISE.ids);

/**
 * Pick an archetype for a block.
 *
 * Landmarks are only in the pool near a core, and the far outskirts get nothing but podiums — a lone
 * spire on the edge of a city reads as the centre and undoes the whole falloff.
 *
 * Exported so `debug/buildings.html` can preview the real distribution. A catalogue that showed a
 * different mix from the one the city uses would be worse than no catalogue.
 *
 * @param {number} roll - A single 0..1 value, so this consumes exactly one number from the block's
 *   stream regardless of which branch it takes. Consuming a variable count would make the layout depend
 *   on coreness, and every city would shift the moment the falloff was retuned.
 * @param {number} coreness - 0 at a city's edge, 1 at its centre.
 * @returns {string} An archetype id.
 */
export function pickCityShape(roll, coreness) {
  if (coreness < CITY.CORE.PODIUM_ONLY_BELOW) return 'podium';

  const includeLandmarks = coreness >= CITY.CORE.LANDMARK_ABOVE;
  const total = COMMON_WEIGHT + (includeLandmarks ? LANDMARK_WEIGHT : 0);

  let target = roll * total;
  for (const entry of CITY_SHAPES.COMMON) {
    target -= entry.weight;
    if (target < 0) return entry.id;
  }

  if (includeLandmarks) {
    for (const entry of CITY_SHAPES.LANDMARKS) {
      target -= entry.weight;
      if (target < 0) return entry.id;
    }
  }

  // Only reachable through floating-point slack at the very top of the range.
  return CITY_SHAPES.COMMON[0].id;
}

/**
 * The footprint a block holds, or null if nothing is built there.
 *
 * **The single authoritative source for where a building sits.** Both `createStructures` and
 * `createReservation` go through it, so the footprint another biome is told to avoid is by construction
 * the same footprint the city will actually build. Deriving that twice is exactly the sort of duplication
 * that drifts and leaves trees inside towers again.
 *
 * The roll order is part of the world's identity: reordering these lines, or inserting a roll before an
 * existing one, changes every city at every seed. Append only.
 *
 * @param {number} structureSeed
 * @param {number} blockX
 * @param {number} blockZ
 * @param {number} weight - City biome weight at the block centre.
 * @returns {{centerX: number, centerZ: number, width: number, depth: number, heightRoll: number,
 *   shapeRoll: number}|null}
 */
function blockFootprint(structureSeed, blockX, blockZ, weight) {
  /*
    One stream per block, keyed by block index rather than by tile. That is what makes the layout
    independent of which tile is being built and of the order tiles are visited in — a city rebuilt an
    hour later is identical.
  */
  const random = createLocalRng(structureSeed, blockX, blockZ);

  const presenceRoll = random();
  const footprintRollX = random();
  const footprintRollZ = random();
  const heightRoll = random();
  const jitterRollX = random();
  const jitterRollZ = random();
  const shapeRoll = random();

  /*
    Density falls off faster than the weight itself, so the outskirts are genuinely sparse rather than a
    slightly thinner downtown.
  */
  const density = CITY.BASE_DENSITY * Math.pow(weight, CITY.DENSITY_FALLOFF);
  if (presenceRoll >= density) return null;

  /** Space inside a block a building may occupy, leaving a full street around it. */
  const interior = CITY.BLOCK_SIZE - CITY.STREET_WIDTH;

  const width = interior * lerp(CITY.FOOTPRINT_MIN, CITY.FOOTPRINT_MAX, footprintRollX);
  const depth = interior * lerp(CITY.FOOTPRINT_MIN, CITY.FOOTPRINT_MAX, footprintRollZ);

  /*
    Jitter is limited to whatever slack remains after reserving half a street on each side. That is what
    preserves the no-intersection guarantee: every building stays at least STREET_WIDTH/2 inside its
    block, so neighbours always have a full street between them and no collision check is needed.

    It also means a building never crosses a block boundary, which is what lets the reservation below
    check only the blocks a point's own radius reaches.
  */
  const slackX = Math.max(0, (CITY.BLOCK_SIZE - width) / 2 - CITY.STREET_WIDTH / 2);
  const slackZ = Math.max(0, (CITY.BLOCK_SIZE - depth) / 2 - CITY.STREET_WIDTH / 2);

  return {
    centerX: (blockX + 0.5) * CITY.BLOCK_SIZE + (jitterRollX * 2 - 1) * slackX,
    centerZ: (blockZ + 0.5) * CITY.BLOCK_SIZE + (jitterRollZ * 2 - 1) * slackZ,
    width,
    depth,
    heightRoll,
    shapeRoll,
  };
}

/** Whether a circle overlaps an axis-aligned rectangle. */
function circleOverlapsRect(x, z, radius, rect) {
  const dx = Math.max(Math.abs(x - rect.centerX) - rect.width / 2, 0);
  const dz = Math.max(Math.abs(z - rect.centerZ) - rect.depth / 2, 0);
  return dx * dx + dz * dz < radius * radius;
}

/**
 * Range of block indices whose centres fall inside a span.
 *
 * Centre of block `i` is `(i + 0.5) * blockSize`, so requiring that to land in
 * `[start, start + length)` gives the bounds below. Getting this wrong by one either duplicates
 * a row of buildings along every tile seam or leaves a gap in the grid.
 */
function blockRange(start, length, blockSize) {
  const first = Math.ceil(start / blockSize - 0.5);
  const last = Math.ceil((start + length) / blockSize - 0.5) - 1;
  return { first, last };
}

export const city = {
  id: 'city',
  label: 'City',

  palette: PALETTE.city,

  suitabilityBias: CITY.SUITABILITY_BIAS,

  /**
   * Build the ground elevation function.
   *
   * Deliberately low relief: a street grid on a steep slope looks wrong. Not flat either,
   * because a perfectly level plane has no cue for distance in a wireframe and reads as a
   * void — a slight roll gives the grid something to drape over.
   *
   * Note that at a boundary with the mountains the blended ground can still be steep, so
   * outlying buildings sit on hillsides. That is left alone: each building's footprint follows
   * the ground at its own corners, so it looks built into the slope rather than broken.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number) => number} Elevation in world units, always >= 0.
   */
  createElevation(seed) {
    const field = createFbm(seed, 'elevation:city', {
      octaves: CITY.OCTAVES,
      baseFrequency: 1 / CITY.WAVELENGTH,
      persistence: CITY.PERSISTENCE,
      lacunarity: CITY.LACUNARITY,
    });

    return function sampleCityElevation(x, z) {
      // Remapped to 0..1 so this biome can never contribute a negative elevation to the blend.
      const unit = (field(x, z) + 1) / 2;
      return unit * CITY.MAX_ELEVATION;
    };
  },

  maxElevation: CITY.MAX_ELEVATION,

  /**
   * Build the query that tells other biomes where buildings are.
   *
   * ## Why this exists
   *
   * Biomes place structures independently and know nothing about each other, which is what keeps them
   * decoupled. At a city/forest border both have weight, so both build — and trees were growing up
   * through towers and clipping into walls.
   *
   * Rejecting overlaps from the collected list would not work: a building belongs to the tile holding its
   * block *centre* and may extend past a tile edge, so a tree in the neighbouring tile would never see it
   * and overlaps would survive along every seam. This instead re-derives the footprint from world
   * coordinates, which gives the same answer no matter which tile is asking.
   *
   * ## What it deliberately does not check
   *
   * Ground slope. A building on ground too steep is skipped, but this still reserves its block, so the
   * occasional clearing has no building in it. That trade buys four fewer terrain samples per query, and
   * a small unexplained gap is invisible next to a tree inside a tower.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number, radius: number, context: object) => boolean}
   */
  createReservation(seed) {
    // The same derived seed as placement, or the reservation would describe a different city.
    const structureSeed = deriveSeed(seed, 'structures:city');

    /**
     * @param {number} x
     * @param {number} z
     * @param {number} radius - Clear space the caller needs, e.g. a tree's crown radius.
     * @param {object} context
     * @param {(x: number, z: number) => number} context.sampleWeight - City weight.
     * @param {Map} [context.cache] - Per-tile memo. Blocks are 220 units and trees are far denser than
     *   that, so without it the same block is re-derived hundreds of times per tile.
     */
    return function reserved(x, z, radius, { sampleWeight, cache }) {
      const margin = radius + CITY.STRUCTURE_CLEARANCE;

      /*
        Only the blocks the margin actually reaches. A building never crosses a block boundary — jitter is
        limited to the slack inside half a street — so nothing outside this range can be close enough.
      */
      const firstX = Math.floor((x - margin) / CITY.BLOCK_SIZE);
      const lastX = Math.floor((x + margin) / CITY.BLOCK_SIZE);
      const firstZ = Math.floor((z - margin) / CITY.BLOCK_SIZE);
      const lastZ = Math.floor((z + margin) / CITY.BLOCK_SIZE);

      for (let blockX = firstX; blockX <= lastX; blockX += 1) {
        for (let blockZ = firstZ; blockZ <= lastZ; blockZ += 1) {
          const key = `${blockX},${blockZ}`;
          let footprint = cache?.get(key);

          if (footprint === undefined) {
            const weight = sampleWeight(
              (blockX + 0.5) * CITY.BLOCK_SIZE,
              (blockZ + 0.5) * CITY.BLOCK_SIZE,
            );
            footprint = weight > 0 ? blockFootprint(structureSeed, blockX, blockZ, weight) : null;
            cache?.set(key, footprint);
          }

          if (footprint && circleOverlapsRect(x, z, margin, footprint)) return true;
        }
      }

      return false;
    };
  },

  /**
   * Build the structure placement function.
   *
   * @param {number} seed - Base world seed.
   * @returns {(region: object, context: object) => Array<object>} Collects the buildings whose
   *   blocks belong to a tile region.
   */
  createStructures(seed) {
    /*
      A dedicated seed, so building placement is statistically independent of the city's own
      suitability field and of its ground. Sharing one would correlate where towers stand with
      where the ground happens to rise.
    */
    const structureSeed = deriveSeed(seed, 'structures:city');

    /**
     * @param {{originX: number, originZ: number, size: number}} region - Tile bounds.
     * @param {object} context
     * @param {(x: number, z: number) => number} context.sampleWeight - City biome weight.
     * @param {(x: number, z: number) => number} context.sampleGround - Ground elevation. Must
     *   be the *drawn* surface, or buildings float above or sink into the visible wireframe.
     * @returns {Array<object>} Building descriptions.
     */
    return function collectStructures(region, { sampleWeight, sampleGround, isReserved }) {
      const xRange = blockRange(region.originX, region.size, CITY.BLOCK_SIZE);
      const zRange = blockRange(region.originZ, region.size, CITY.BLOCK_SIZE);

      const buildings = [];

      for (let blockX = xRange.first; blockX <= xRange.last; blockX += 1) {
        for (let blockZ = zRange.first; blockZ <= zRange.last; blockZ += 1) {
          const blockCenterX = (blockX + 0.5) * CITY.BLOCK_SIZE;
          const blockCenterZ = (blockZ + 0.5) * CITY.BLOCK_SIZE;

          const weight = sampleWeight(blockCenterX, blockCenterZ);
          if (weight <= 0) continue;

          const footprint = blockFootprint(structureSeed, blockX, blockZ, weight);
          if (!footprint) continue;

          const { centerX, centerZ, width, depth, heightRoll, shapeRoll } = footprint;

          /*
            How close to the middle of the city this block is, 0 at the edge and 1 at the core. Derived
            from the biome weight that already exists, so a downtown costs no extra noise sampling —
            the shape of a city's core is the shape of its region.
          */
          const coreness = Math.pow(Math.min(1, weight), CITY.CORE.SHARPNESS);

          const shape = pickCityShape(shapeRoll, coreness);

          // Skewed so tall towers are rare and a skyline has landmarks rather than a flat wall.
          const shaped = Math.pow(heightRoll, CITY.HEIGHT_SKEW);
          const fullHeight = lerp(CITY.MIN_HEIGHT, CITY.MAX_HEIGHT, shaped);

          /*
            Height falls off from the core, which is what turns a uniform field of towers into a skyline.

            **One falloff, not two.** Coreness is already derived from the biome weight, so scaling by
            weight as well would apply the same signal twice and collapse the outskirts to nothing.
            `MIN_HEIGHT_SCALE` is the height available at the very edge of a city — low buildings, not
            invisible ones.
          */
          const coreScale = lerp(CITY.MIN_HEIGHT_SCALE, 1, coreness);

          // Shapes that stop being themselves when stretched are capped rather than scaled.
          const lowRiseCap = LOW_RISE.has(shape) ? CITY_SHAPES.LOW_RISE.HEIGHT_CAP : 1;

          const height = fullHeight * coreScale * lowRiseCap;

          /*
            Ground sampled at each corner so a footprint on a slope follows the terrain. The
            roof sits level above the highest corner: buildings are built level even where the
            ground is not, and anchoring to the highest corner keeps the whole base above it.
          */
          const halfWidth = width / 2;
          const halfDepth = depth / 2;
          const cornerGroundY = CORNER_OFFSETS.map(([sx, sz]) =>
            sampleGround(centerX + sx * halfWidth, centerZ + sz * halfDepth),
          );
          const highestCorner = Math.max(...cornerGroundY);
          const lowestCorner = Math.min(...cornerGroundY);

          /*
            Refuse steep ground. Checked after every roll is consumed, so rejecting a block does
            not shift the random sequence for any other block — placement stays identical to what
            it would have been, minus the buildings on cliffs.
          */
          const run = Math.max(width, depth);
          if ((highestCorner - lowestCorner) / run > CITY.MAX_GROUND_SLOPE) continue;

          /*
            Keep out of another biome's reserved ground — which above all means **out of the water**.

            **This check was missing entirely**, and it is why a tower was seen standing in a lake. Every other structure
            in the world consulted the cross-biome reservation; the city, which publishes one of its own, never consulted
            anyone else's. At a city border with a river or a lakeland both biomes have weight, so the water gets drawn
            from the water biome's side while the tower was placed regardless.

            One test at the centre with the footprint's circumscribing radius, rather than four corner tests. A tower is
            a compact box, so the circle is barely larger than the footprint, and this is a per-block cost on the second
            most expensive tile in the world.

            Checked after every roll is consumed, so rejecting a block does not shift the random sequence for any other —
            placement stays identical to what it would have been, minus the buildings in the water.
          */
          if (isReserved?.(centerX, centerZ, Math.hypot(width, depth) / 2)) continue;

          const roofY = highestCorner + height;

          const accentRings =
            height >= CITY.ACCENT_MIN_HEIGHT
              ? CITY.ACCENT_RINGS.map((fraction) => highestCorner + height * fraction)
              : [];

          const antenna =
            height >= CITY.ANTENNA_MIN_HEIGHT
              ? { fromY: roofY, toY: roofY + height * CITY.ANTENNA_LENGTH }
              : null;

          /*
            Ground at the points this archetype actually stands on, which are not always the four
            rectangular corners: a cylinder's base is an octagon inside the rectangle, a twist is rotated,
            and twin towers have two separate footprints. Sampling only the corners and guessing the rest
            put base vertices below the drawn surface — buildings visibly sinking into the terrain.
          */
          const shapeSpec = {
            centerX,
            centerZ,
            width,
            depth,
            baseY: highestCorner,
            height,
          };
          const baseGroundY = baseFootprintPoints(getBuildingShapeOrDefault(shape), shapeSpec).map(
            ([px, pz]) => [px, pz, sampleGround(px, pz)],
          );

          buildings.push({
            kind: 'tower',
            shape,
            centerX,
            centerZ,
            width,
            depth,
            height,
            cornerGroundY,
            baseGroundY,
            roofY,
            accentRings,
            antenna,
            weight,
            coreness,
          });
        }
      }

      return buildings;
    };
  },
};
